<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Button, Dialog, Textarea, Message, ToggleSwitch, InputText, MultiSelect, DataTable, Column, type DataTableCellEditCompleteEvent, type DataTableRowReorderEvent } from 'primevue';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, ref } from 'vue';
import type { ProductionOrderRecipe } from 'shared/productionOrders/domain/productionOrder';
import { useProductStore } from 'shared/products/stores/productStore';
import type { Product } from 'shared/products/domain/product';

const visible = ref(false);
const problems = ref<ProblemDetails>();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const productStore = useProductStore();
const productOptions = computed<Product[]>(() => {
  const products = productStore.sortedByName;
  return products;
});

const current = ref<ProductionOrderRecipe | null>();

defineExpose({
  show
});

const emit = defineEmits<{
  confirm: [recipe: ProductionOrderRecipe]
}>();

const name = ref<string>('');
const nameError = computed(() => {
  if (name.value === '') {
    return translations.value.error.nameIsRequired;
  }

  return undefined;
});
const nameChanged = computed(() => {
  if (!current.value) return false;
  return current.value.name !== name.value;
});

const instruction = ref<string>('');
const instructionChanged = computed(() => {
  if (!current.value) return false;
  return current.value.instruction !== instruction.value;
});

const machineInstruction = ref<string>('');
const machineInstructionChanged = computed(() => {
  if (!current.value) return false;
  return current.value.machineInstruction !== machineInstruction.value;
});

const consumeEntireStock = ref<boolean>(false);
const consumeEntireStockChanged = computed(() => {
  if (!current.value) return false;
  return current.value.consumeEntireStock !== consumeEntireStock.value;
});

const inProducts = ref<InProduct[]>([]);
const outProducts = ref<OutProduct[]>([]);

interface ShowOptions {
  recipe: ProductionOrderRecipe | null;
}

function show(options: ShowOptions) {
  current.value = options.recipe;

  if (!current.value) {
    current.value = {
      id: crypto.randomUUID(),
      name: '',
      row: 0,
      description: '',
      instruction: '',
      machineInstruction: '',
      consumeEntireStock: false,
      inProducts: [],
      outProducts: [],
    };
  }

  name.value = current.value.name;
  consumeEntireStock.value = current.value.consumeEntireStock;

  inProducts.value = [];
  for (const inProduct of current.value.inProducts.sort((a, b) => a.order - b.order)) {
    inProducts.value.push({
      id: inProduct.id,
      product: getProductNameById(inProduct.productId),
      amount: inProduct.amount.toString(),
      consumeEntireStock: inProduct.consumeEntireStock,
    });
  }

  outProducts.value = [];
  for (const outProduct of current.value.outProducts.sort((a, b) => a.order - b.order)) {
    outProducts.value.push({
      id: outProduct.id,
      product: getProductNameById(outProduct.productId),
      amount: outProduct.amount.toString()
    });
  }

  visible.value = true;
}

function onConfirmClick() {
  if (nameError.value) {
    return;
  }

  const recipe = getRecipe();
  emit('confirm', recipe);
  visible.value = false;
}

function getRecipe() {
  if (!current.value) {
    throw new Error('No current recipe');
  }

  const result = {
    ...current.value
  };

  result.name = name.value;
  result.instruction = instruction.value;
  result.machineInstruction = machineInstruction.value;
  result.consumeEntireStock = consumeEntireStock.value;

  result.inProducts = [];
  let order = 0;
  for (const inProduct of inProducts.value) {
    const productId = productStore.products.find(p => p.name === inProduct.product)?.id;
    if (!productId) {
      console.log(`Product not found: ${inProduct.product}`);
      continue;
    }

    result.inProducts.push({
      id: inProduct.id,
      productId: productId,
      amount: parseInt(inProduct.amount, 10),
      amountDone: 0,
      consumeEntireStock: inProduct.consumeEntireStock,
      order: order++,
      instruction: '',
      machineInstruction: ''
    });
  }

  order = 0;
  result.outProducts = [];
  for (const outProduct of outProducts.value) {
    const productId = productStore.products.find(p => p.name === outProduct.product)?.id;
    if (!productId) {
      console.log(`Product not found: ${outProduct.product}`);
      continue;
    }

    result.outProducts.push({
      id: outProduct.id,
      productId: productId,
      amount: parseInt(outProduct.amount, 10),
      amountDone: 0,
      order: order++,
      instruction: '',
      machineInstruction: ''
    });
  }

  return result;
}

const selectedInProducts = ref<Product[]>([]);
function addInProduct() {
  if (!current.value) return;

  for (const product of selectedInProducts.value) {
    const existing = inProducts.value.find(p => p.product === product.name);
    if (!existing) {
      const newInProduct = {
        id: crypto.randomUUID(),
        product: product.name,
        amount: '0',
        consumeEntireStock: false,
      };
      inProducts.value.push(newInProduct);
    }
  }

  selectedInProducts.value = [];
}

function onInProductReorder(event: DataTableRowReorderEvent) {
  inProducts.value = event.value;
}

const selectedOutProducts = ref<Product[]>([]);
function addOutProduct() {
  if (!current.value) return;

  for (const product of selectedOutProducts.value) {
    const existing = outProducts.value.find(p => p.product === product.name);
    if (!existing) {
      const newOutProduct = {
        id: crypto.randomUUID(),
        product: product.name,
        amount: '0'
      };
      outProducts.value.push(newOutProduct);
    }
  }

  selectedOutProducts.value = [];
}

function onOutProductReorder(event: DataTableRowReorderEvent) {
  outProducts.value = event.value;
}

function getProductNameById(id: string): string {
  const product = productStore.getById(id);
  return product ? product.name : 'Unknown product';
}

function deleteInProduct(id: string) {
  if (!current.value) return;
  inProducts.value.splice(inProducts.value.findIndex(p => p.id === id), 1);
}

function deleteOutProduct(id: string) {
  if (!current.value) return;
  outProducts.value.splice(outProducts.value.findIndex(p => p.id === id), 1);
}

interface InProduct {
  id: string;
  product: string;
  amount: string;
  consumeEntireStock: boolean;
}

interface OutProduct {
  id: string;
  product: string;
  amount: string;
}

const onCellEditComplete = (event: DataTableCellEditCompleteEvent) => {
  const { data, newData, field } = event;

  switch (field) {
    case 'amount':
      if (isPositiveInteger(newData.value)) {
        data[field] = newData.value;
      }
      else {
        event.originalEvent.preventDefault();
      }
      break;
    case 'consumeEntireStock':
      data[field] = newData.value;
      break;
    default:
      event.originalEvent.preventDefault();
      break;
  }
};

const isPositiveInteger = (val: string) => {
  let str = String(val);

  str = str.trim();

  if (!str) {
    return false;
  }

  str = str.replace(/^0+/, '') || '0';
  const n = Math.floor(Number(str));

  return n !== Infinity && String(n) === str && n >= 0;
};

</script>

<template>
  <Dialog class="edit-recipe-dialog"
    :header="current ? translations.productionOrders.editPage.editRecipeTitle : translations.productionOrders.editPage.addRecipeTitle"
    v-model:visible="visible">
    <ProblemDetailsView v-model="problems"></ProblemDetailsView>
    <div class="recipe-form">
      <div class="field" :class="{ changed: nameChanged }">
        <label for="edit-recipe-name">{{ translations.generic.name }}</label>
        <InputText autocomplete="off" autofocus :invalid="nameError !== undefined" id="edit-recipe-name"
          v-model:model-value="name"></InputText>
        <Message v-if="nameError" severity="error" variant="simple">{{ nameError }}</Message>
      </div>

      <div class="field" :class="{ changed: instructionChanged }">
        <label for="edit-recipe-instruction">{{
          translations.productionOrders.editPage.recipeInstruction }}</label>
        <Textarea autocomplete="off" id="edit-recipe-instruction" v-model:model-value="instruction"></Textarea>
      </div>

      <div class="field" :class="{ changed: machineInstructionChanged }">
        <label for="edit-recipe-machineinstruction">{{
          translations.productionOrders.editPage.recipeMachineInstruction
          }}</label>
        <Textarea autocomplete="off" id="edit-recipe-machineinstruction"
          v-model:model-value="machineInstruction"></Textarea>
      </div>

      <div class="toggle-field" :class="{ changed: consumeEntireStockChanged }">
        <ToggleSwitch v-model:modelValue="consumeEntireStock" id="edit-recipe-consume-entire-stock">
        </ToggleSwitch>
        <label for="edit-recipe-consume-entire-stock">{{
          translations.productionOrders.editPage.consumeEntireStock }}</label>
      </div>

      <hr />
      <div class="products-section">
        <h3>{{ translations.productionOrders.editPage.inProductsTitle }}</h3>
        <div class="products-actions flex gap-2">
          <MultiSelect autocomplete="off" id="multiselect-in-products" name="in-products" :options="productOptions"
            class="flex-1 overflow-hidden" :maxSelectedLabels="1" optionLabel="name" v-model="selectedInProducts"
            filter>
          </MultiSelect>
          <Button type="button" class="flex-none" :label="translations.generic.add" icon="pi pi-plus"
            @click="addInProduct"></Button>
        </div>

        <DataTable class="products-table" :value="inProducts" editMode="cell" @cell-edit-complete="onCellEditComplete"
          @rowReorder="onInProductReorder">
          <template #empty>{{ translations.productionOrders.editPage.noProducts }}</template>
          <Column rowReorder headerStyle="width: 3rem" :reorderableColumn="false" />
          <Column field="product" class="inproduct-name" :header="translations.generic.product"></Column>
          <Column field="amount" class="inproduct-amount" :header="translations.generic.amount">
            <template #editor="{ data }">
              <InputText type="text" v-model="data.value"></InputText>
            </template>
          </Column>
          <Column field="consumeEntireStock" class="inproduct-consume-entire-stock"
            :header="translations.productionOrders.editPage.consumeEntireStock">
            <template #editor="{ data }">
              <ToggleSwitch v-model:modelValue="data.value"></ToggleSwitch>
            </template>
          </Column>
          <Column>
            <template #body="products">
              <Button type="button" icon="pi pi-trash" class="p-button-text p-button-danger"
                @click="deleteInProduct(products.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </div>
      <div class="products-section">
        <h3>{{ translations.productionOrders.editPage.outProductsTitle }}</h3>
        <div class="products-actions flex gap-2">
          <MultiSelect autocomplete="off" id="multiselect-out-products" name="out-products" :options="productOptions"
            optionLabel="name" :maxSelectedLabels="1" v-model="selectedOutProducts" filter class="flex-1">
          </MultiSelect>
          <Button type="button" class="flex-none" :label="translations.generic.add" icon="pi pi-plus"
            @click="addOutProduct"></Button>
        </div>

        <DataTable class="products-table" :value="outProducts" editMode="cell" @cell-edit-complete="onCellEditComplete"
          @rowReorder="onOutProductReorder">
          <template #empty>{{ translations.productionOrders.editPage.noProducts }}</template>
          <Column rowReorder headerStyle="width: 3rem" :reorderableColumn="false" />
          <Column field="product" class="inproduct-name" :header="translations.generic.product"></Column>
          <Column field="amount" class="inproduct-amount" :header="translations.generic.amount">
            <template #editor="{ data }">
              <InputText type="text" v-model="data.value"></InputText>
            </template>
          </Column>
          <Column>
            <template #body="products">
              <Button type="button" icon="pi pi-trash" class="p-button-text p-button-danger"
                @click="deleteOutProduct(products.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
    <Button :label="translations.generic.save" @click="onConfirmClick"></Button>
  </Dialog>
</template>

<style scoped>
.recipe-form {
  display: grid;
  row-gap: .5rem;
  margin-bottom: .5rem;
}

.products-section {
  margin-top: .5rem;
}

.products-actions {
  margin-bottom: .5rem;
  max-width: 100%;
}

.no-products {
  color: #666;
  padding: .25rem 0;
}

.edit-recipe-dialog {
  min-width: 600px;
}
</style>
